// frontend-public/src/pages/BookingPage.js
// ============================================================
// Online Web Booking — 公開訂位頁（Plan 090 / 086 Phase 1a.0）
// 路由：/r/:slug   單欄、由上往下捲動的一頁式表單（非雙欄、非精靈分頁）
//
//   餐廳資訊 → 訂位日期(月曆，今天起 3 個月) → 大人/小孩 → 時段 → 加購
//   → 姓名/電話 → LINE 通知(勾選) → 同意聲明 → 確認訂位 → 訂位成功卡
//
// 桌機：置中單欄卡片；手機：全寬。字級一律 ≥16px。LINE 綁定為 1a.1（LIFF）。
// ============================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Button, Chip, IconButton, TextField, Link,
  Checkbox, FormControlLabel, Stack, CircularProgress, Alert, Paper, Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PlaceIcon from '@mui/icons-material/Place';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import bookingService from '../services/bookingService';

const GREEN = '#06C755';
const GREEN_D = '#05b34c';
const COMPANY = '捷思博科技股份有限公司';

// ---- 日期工具（UTC 錨定，避開時區換日）----
const pad2 = (n) => String(n).padStart(2, '0');
const todayTaipei = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Taipei' });
const addDaysISO = (iso, days) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};
const isoOf = (y, m, d) => `${y}-${pad2(m + 1)}-${pad2(d)}`;        // m: 0-11
const monthDaysCount = (y, m) => new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
const firstWeekday = (y, m) => new Date(Date.UTC(y, m, 1)).getUTCDay(); // 0=Sun
const WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六'];
const niceDate = (iso, today) => {
  if (!iso) return '';
  const off = Math.round((new Date(`${iso}T00:00:00Z`) - new Date(`${today}T00:00:00Z`)) / 86400000);
  const [, m, d] = iso.split('-');
  const wd = WEEKDAYS[new Date(`${iso}T00:00:00Z`).getUTCDay()];
  const md = `${parseInt(m, 10)}/${parseInt(d, 10)}`;
  if (off === 0) return `今天 ${md}`;
  if (off === 1) return `明天 ${md}`;
  return `${md}（週${wd}）`;
};

const formatHours = (bh) =>
  Array.isArray(bh) && bh.length ? bh.map((w) => `${w.start}–${w.end}`).join(' · ') : null;

// 時段依營業時段(window)分組（午餐/晚餐…），>1 具名 window 時才顯示分組標籤。
const groupSlots = (slots, businessHours) => {
  if (!Array.isArray(businessHours) || businessHours.filter((w) => w && w.name).length < 2) {
    return [{ name: null, slots }];
  }
  const groups = businessHours.map((w) => ({ name: w.name || '', start: w.start, end: w.end, slots: [] }));
  const other = { name: null, slots: [] };
  for (const s of slots) {
    const g = groups.find((w) => s.time >= w.start && s.time <= w.end);
    (g || other).slots.push(s);
  }
  const out = groups.filter((g) => g.slots.length).map((g) => ({ name: g.name, slots: g.slots }));
  if (other.slots.length) out.push({ name: null, slots: other.slots });
  return out;
};

const ERROR_MESSAGES = {
  FULL: '該時段已額滿，請選擇其他時間',
  CLOSED: '所選時間非營業時段',
  PARTY_SIZE: '人數超出可訂範圍',
  OUT_OF_WINDOW: '所選日期不在可訂範圍',
  BAD_PHONE: '電話格式不正確，請重新輸入',
  BAD_REQUEST: '請填寫完整資料',
  RATE_LIMITED: '操作太頻繁，請稍候再試',
  SERVER_ERROR: '系統忙線中，請稍後再試'
};

const Stepper = ({ value, min, max, onChange }) => (
  <Stack direction="row" alignItems="center" spacing={1.5}>
    <IconButton size="small" disabled={value <= min} onClick={() => onChange(value - 1)}
      sx={{ border: '1px solid', borderColor: 'divider' }}>
      <RemoveIcon fontSize="small" />
    </IconButton>
    <Typography sx={{ minWidth: 30, textAlign: 'center', fontSize: 20, fontWeight: 600 }}>{value}</Typography>
    <IconButton size="small" disabled={value >= max} onClick={() => onChange(value + 1)}
      sx={{ border: '1px solid', borderColor: 'divider' }}>
      <AddIcon fontSize="small" />
    </IconButton>
  </Stack>
);

const InfoRow = ({ icon, text }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
    {React.cloneElement(icon, { sx: { fontSize: 19 } })}
    <Typography sx={{ fontSize: 16 }}>{text}</Typography>
  </Stack>
);

const Label = ({ children, sx }) => (
  <Typography sx={{ fontWeight: 700, fontSize: 17, mb: 1, ...sx }}>{children}</Typography>
);

// 單欄可捲動外層。必須 module-level（不可定義在 BookingPage 內）——否則每次 render 換新
// 元件 identity → 子樹 remount → 打字時 TextField 失焦。
const Page = ({ children }) => (
  <Box sx={{ minHeight: '100vh', bgcolor: '#eef1f5', py: { xs: 0, sm: 4 } }}>
    <Box sx={{
      width: '100%', maxWidth: 600, mx: 'auto', bgcolor: '#fff',
      borderRadius: { xs: 0, sm: 3 }, overflow: 'hidden',
      boxShadow: { xs: 'none', sm: '0 8px 30px rgba(0,0,0,.08)' }, minHeight: { xs: '100vh', sm: 'auto' }
    }}>
      {children}
    </Box>
  </Box>
);

const BookingPage = () => {
  const { slug } = useParams();
  const today = todayTaipei();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [info, setInfo] = useState(null);

  const [selectedDate, setSelectedDate] = useState(today);
  const [adults, setAdults] = useState(2);
  const [childrenCount, setChildrenCount] = useState(0);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);
  const [addonValues, setAddonValues] = useState({});
  const [selectedReqs, setSelectedReqs] = useState([]); // 特殊需求標記（多選）
  const [note, setNote] = useState('');                 // 備註（自由文字）
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');             // 選填，僅當餐廳開啟收集 Email 時顯示
  const [lineNotify, setLineNotify] = useState(true); // LINE 訂位通知，預設勾選（1a.1 LIFF 綁定時生效）
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [confirmation, setConfirmation] = useState(null);

  // 月曆檢視月份
  const [viewY, setViewY] = useState(() => parseInt(today.slice(0, 4), 10));
  const [viewM, setViewM] = useState(() => parseInt(today.slice(5, 7), 10) - 1);

  const partySize = adults + childrenCount;
  const maxParty = info?.config?.maxPartySize || 10;
  const windowDays = info?.config?.bookingWindowDays || 90;
  const maxDate = addDaysISO(today, windowDays);
  const curY = parseInt(today.slice(0, 4), 10);
  const curM = parseInt(today.slice(5, 7), 10) - 1;

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const data = await bookingService.getRestaurant(slug);
        if (alive) setInfo(data);
      } catch (err) {
        if (alive) setNotFound(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  const loadAvailability = useCallback(async (date, party) => {
    setSlotsLoading(true);
    setSelectedTime(null);
    try {
      const data = await bookingService.getAvailability(slug, date, party);
      setSlots(Array.isArray(data?.slots) ? data.slots : []);
    } catch (err) {
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    if (info && !confirmation) loadAvailability(selectedDate, partySize);
  }, [info, confirmation, selectedDate, partySize, loadAvailability]);

  const slotGroups = useMemo(() => groupSlots(slots, info?.businessHours), [slots, info]);

  const calendarCells = useMemo(() => {
    const lead = firstWeekday(viewY, viewM);
    const n = monthDaysCount(viewY, viewM);
    const cells = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let d = 1; d <= n; d++) cells.push(d);
    return cells;
  }, [viewY, viewM]);

  const prevDisabled = viewY < curY || (viewY === curY && viewM <= curM);
  const nm = viewM === 11 ? 0 : viewM + 1;
  const ny = viewM === 11 ? viewY + 1 : viewY;
  const nextDisabled = `${ny}-${pad2(nm + 1)}-01` > maxDate;
  const goPrev = () => { let m = viewM - 1, y = viewY; if (m < 0) { m = 11; y -= 1; } setViewM(m); setViewY(y); };
  const goNext = () => { let m = viewM + 1, y = viewY; if (m > 11) { m = 0; y += 1; } setViewM(m); setViewY(y); };

  const setAdultsSafe = (v) => setAdults(Math.max(1, Math.min(v, maxParty - childrenCount)));
  const setChildrenSafe = (v) => setChildrenCount(Math.max(0, Math.min(v, maxParty - adults)));

  const phoneDigits = (phone || '').replace(/\D/g, '');
  const canSubmit = !!selectedTime && name.trim().length > 0 && phoneDigits.length >= 8 && !submitting;

  const toggleAddon = (key, type) => (e) =>
    setAddonValues((prev) => ({ ...prev, [key]: type === 'number' ? e : e.target.checked }));

  const toggleReq = (tag) =>
    setSelectedReqs((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));

  const handleSubmit = async () => {
    setSubmitError('');
    setSubmitting(true);
    try {
      const extras = {};
      (info.addons || []).forEach((a) => {
        const v = addonValues[a.key];
        if (a.type === 'number') { if (v > 0) extras[a.key] = v; }
        else if (v) extras[a.key] = a.label || true;
      });
      const data = await bookingService.createReservation(slug, {
        date: selectedDate, time: selectedTime, partySize, adults, children: childrenCount,
        name: name.trim(), phone: phoneDigits, extras,
        specialRequests: selectedReqs, note: note.trim() || undefined,
        email: email.trim() || undefined, lineNotify
      });
      setConfirmation(data.reservation);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const body = err?.response?.data || {};
      const code = body.error;
      if (code === 'DUPLICATE') {
        const ex = body.existing;
        setSubmitError(ex ? `您已有訂位（${ex.date} ${ex.time}），請勿重複預訂` : '您已有有效訂位，請勿重複預訂');
      } else {
        setSubmitError(ERROR_MESSAGES[code] || '訂位失敗，請稍後再試');
        if (code === 'FULL') loadAvailability(selectedDate, partySize);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resetAll = () => {
    setConfirmation(null);
    setSelectedTime(null);
    setAdults(2);
    setChildrenCount(0);
    setAddonValues({});
    setSelectedReqs([]);
    setNote('');
    setName('');
    setPhone('');
    setEmail('');
    setLineNotify(true);
    setSubmitError('');
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#eef1f5' }}>
        <CircularProgress sx={{ color: GREEN }} />
      </Box>
    );
  }
  if (notFound) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#eef1f5', p: 3 }}>
        <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 360, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>此餐廳尚未開放網路訂位</Typography>
          <Typography sx={{ fontSize: 16 }} color="text.secondary">請確認連結是否正確，或直接來電預訂。</Typography>
        </Paper>
      </Box>
    );
  }

  if (confirmation) {
    return (
      <Page>
        <Box sx={{ p: { xs: 3, sm: '40px 36px' } }}>
          <Box sx={{ textAlign: 'center', pt: 2, pb: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: GREEN }} />
            <Typography variant="h5" sx={{ mt: 1, fontWeight: 700 }}>訂位成功</Typography>
          </Box>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>{info.restaurant?.name}</Typography>
            <Stack spacing={1}>
              <Row label="日期時間" value={`${niceDate(confirmation.date, today)} · ${confirmation.time}`} />
              <Row label="人數" value={`${confirmation.partySize} 位`} />
              {confirmation.tableName && <Row label="桌位" value={confirmation.tableName} emphasize />}
            </Stack>
          </Paper>
          {/* 1a.1 LINE 綁定後此處改為「確認已發送到您的 LINE」 */}
          <Alert severity="info" sx={{ mt: 2, borderRadius: 2, fontSize: 16 }}>訂位已成立。如需異動或取消，請來電與餐廳聯繫。</Alert>
          <Button fullWidth variant="text" onClick={resetAll} sx={{ mt: 2, color: GREEN, fontSize: 16 }}>再訂一筆</Button>
        </Box>
      </Page>
    );
  }

  return (
    <Page>
      {/* 餐廳封面圖（後台可設定；未設定則不顯示） */}
      {info.restaurant?.coverImageUrl && (
        <Box sx={{
          width: '100%', height: { xs: 180, sm: 230 },
          backgroundImage: `url(${info.restaurant.coverImageUrl})`,
          backgroundSize: 'cover', backgroundPosition: 'center', bgcolor: '#e5e7eb'
        }} />
      )}
      {/* 餐廳資訊 */}
      <Box sx={{ p: { xs: '24px 22px 16px', sm: '30px 36px 18px' } }}>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>{info.restaurant?.name}</Typography>
        <Stack spacing={0.6} sx={{ mt: 1.2 }}>
          {info.restaurant?.address && <InfoRow icon={<PlaceIcon />} text={info.restaurant.address} />}
          {info.restaurant?.phone && <InfoRow icon={<PhoneIcon />} text={info.restaurant.phone} />}
          {formatHours(info.businessHours) && <InfoRow icon={<AccessTimeIcon />} text={formatHours(info.businessHours)} />}
        </Stack>
      </Box>
      <Divider />

      <Box sx={{ p: { xs: 3, sm: '24px 36px 40px' } }}>
        {/* 訂位日期 — 月曆，今天起 3 個月 */}
        <Label>訂位日期</Label>
        <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Button size="small" disabled={prevDisabled} onClick={goPrev} startIcon={<ChevronLeftIcon />}
              sx={{ color: 'text.primary', fontSize: 16, textTransform: 'none', minWidth: 0, px: 0.5 }}>上個月</Button>
            <Typography sx={{ fontWeight: 700, fontSize: 17 }}>{viewY} 年 {viewM + 1} 月</Typography>
            <Button size="small" disabled={nextDisabled} onClick={goNext} endIcon={<ChevronRightIcon />}
              sx={{ color: 'text.primary', fontSize: 16, textTransform: 'none', minWidth: 0, px: 0.5 }}>下個月</Button>
          </Stack>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center', mb: 0.5 }}>
            {WEEKDAYS.map((w) => <Typography key={w} sx={{ fontSize: 16 }} color="text.secondary">{w}</Typography>)}
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
            {calendarCells.map((cell, i) => {
              if (cell === null) return <Box key={`e${i}`} sx={{ height: 40 }} />;
              const iso = isoOf(viewY, viewM, cell);
              const selectable = iso >= today && iso <= maxDate;
              const sel = iso === selectedDate;
              return (
                <Box key={iso}
                  onClick={() => selectable && setSelectedDate(iso)}
                  sx={{
                    height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: 2, fontSize: 16, cursor: selectable ? 'pointer' : 'default',
                    color: sel ? '#fff' : (selectable ? 'text.primary' : 'text.disabled'),
                    bgcolor: sel ? GREEN : 'transparent',
                    '&:hover': selectable && !sel ? { bgcolor: '#f0f0f0' } : {}
                  }}>
                  {cell}
                </Box>
              );
            })}
          </Box>
        </Paper>

        {/* 用餐人數 — 大人 / 小孩 直接在這頁 */}
        <Label>用餐人數</Label>
        <Stack spacing={1.5} sx={{ mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography sx={{ fontSize: 16 }}>大人</Typography>
              <Typography sx={{ fontSize: 16 }} color="text.secondary">12 歲以上</Typography>
            </Box>
            <Stepper value={adults} min={1} max={maxParty - childrenCount} onChange={setAdultsSafe} />
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography sx={{ fontSize: 16 }}>小孩</Typography>
              <Typography sx={{ fontSize: 16 }} color="text.secondary">12 歲以下</Typography>
            </Box>
            <Stepper value={childrenCount} min={0} max={maxParty - adults} onChange={setChildrenSafe} />
          </Stack>
          <Typography sx={{ fontSize: 16 }} color="text.secondary">共 {partySize} 位（每筆最多 {maxParty} 位）</Typography>
        </Stack>

        {/* 時段 */}
        <Label>時段</Label>
        {slotsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} sx={{ color: GREEN }} /></Box>
        ) : slots.length === 0 ? (
          <Typography sx={{ fontSize: 16, py: 2 }} color="text.secondary">此日期暫無可訂時段</Typography>
        ) : (
          slotGroups.map((g, gi) => (
            <Box key={g.name || `g${gi}`} sx={{ mb: 1.5 }}>
              {g.name && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: gi ? 2 : 0, mb: 1 }}>
                  <Typography sx={{ fontSize: 16 }} color="text.secondary">{g.name}</Typography>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                </Box>
              )}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(4, 1fr)', sm: 'repeat(6, 1fr)' }, gap: 1 }}>
                {g.slots.map((s) => {
                  const active = s.time === selectedTime;
                  return (
                    <Chip key={s.time} label={s.time} disabled={!s.available}
                      onClick={() => s.available && setSelectedTime(s.time)}
                      sx={{
                        height: 46, borderRadius: 2, fontSize: 16,
                        textDecoration: s.available ? 'none' : 'line-through',
                        bgcolor: active ? GREEN : '#fff', color: active ? '#fff' : 'text.primary',
                        border: '1px solid', borderColor: active ? GREEN : 'divider',
                        '&:hover': { bgcolor: active ? GREEN : '#f0f0f0' }
                      }} />
                  );
                })}
              </Box>
            </Box>
          ))
        )}

        {/* 加購 */}
        {Array.isArray(info.addons) && info.addons.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Label>加購</Label>
            <Stack spacing={1}>
              {info.addons.map((a) => (
                a.type === 'number' ? (
                  <Stack key={a.key} direction="row" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ fontSize: 16 }} color="text.secondary">{a.label}</Typography>
                    <Stepper value={addonValues[a.key] || 0} min={0} max={20} onChange={toggleAddon(a.key, 'number')} />
                  </Stack>
                ) : (
                  <FormControlLabel key={a.key}
                    control={<Checkbox checked={!!addonValues[a.key]} onChange={toggleAddon(a.key)} sx={{ '&.Mui-checked': { color: GREEN } }} />}
                    label={<Typography sx={{ fontSize: 16 }}>{a.label}</Typography>} />
                )
              ))}
            </Stack>
          </Box>
        )}

        {/* 特殊需求（後台自訂標記，多選） */}
        {Array.isArray(info.specialRequestTags) && info.specialRequestTags.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Label>特殊需求</Label>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {info.specialRequestTags.map((tag) => {
                const on = selectedReqs.includes(tag);
                return (
                  <Chip key={tag} label={tag} onClick={() => toggleReq(tag)}
                    sx={{
                      height: 40, fontSize: 16, borderRadius: 2,
                      bgcolor: on ? GREEN : '#fff', color: on ? '#fff' : 'text.primary',
                      border: '1px solid', borderColor: on ? GREEN : 'divider',
                      '&:hover': { bgcolor: on ? GREEN : '#f0f0f0' }
                    }} />
                );
              })}
            </Box>
          </Box>
        )}

        {/* 備註（選填） */}
        <Label sx={{ mt: 3 }}>備註（選填）</Label>
        <TextField value={note} onChange={(e) => setNote(e.target.value)} fullWidth multiline minRows={2}
          placeholder="如有其他需求請告訴我們"
          sx={{ mb: 1, '& .MuiInputBase-input': { fontSize: 16 } }} />

        {/* 聯絡資料 */}
        <Label sx={{ mt: 3 }}>聯絡資料</Label>
        <Stack spacing={2} sx={{ mb: 2 }}>
          <TextField label="姓名" value={name} onChange={(e) => setName(e.target.value)} fullWidth required
            sx={{ '& .MuiInputBase-input': { fontSize: 16 }, '& label': { fontSize: 16 } }} />
          <TextField label="手機號碼" value={phone} onChange={(e) => setPhone(e.target.value)} fullWidth required type="tel" placeholder="0912345678"
            sx={{ '& .MuiInputBase-input': { fontSize: 16 }, '& label': { fontSize: 16 } }} />
          {info.config?.collectEmail && (
            <TextField label="Email（選填）" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth type="email" placeholder="you@example.com"
              sx={{ '& .MuiInputBase-input': { fontSize: 16 }, '& label': { fontSize: 16 } }} />
          )}
        </Stack>

        {/* LINE 訂位通知（勾選，預設開啟；1a.1 LIFF 綁定後生效） */}
        <FormControlLabel
          sx={{ mb: 2, alignItems: 'flex-start' }}
          control={<Checkbox checked={lineNotify} onChange={(e) => setLineNotify(e.target.checked)} sx={{ pt: 0.25, '&.Mui-checked': { color: GREEN } }} />}
          label={<Typography sx={{ fontSize: 16 }}>接收 LINE 訂位通知與用餐提醒</Typography>} />

        {/* 同意聲明 */}
        <Typography sx={{ fontSize: 16, color: 'text.secondary', lineHeight: 1.7, mb: 2 }}>
          點選「確認訂位」即表示您同意將上述訂位資訊及姓名、電話等聯絡資料，提供予「{info.restaurant?.name}」及其訂位系統服務商{COMPANY}，作為訂位確認與後續聯繫之用。詳見
          <Link href="/privacy" target="_blank" rel="noopener" sx={{ color: GREEN }}>隱私權政策</Link>。
        </Typography>

        {submitError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2, fontSize: 16 }}>{submitError}</Alert>}

        <Button fullWidth variant="contained" disabled={!canSubmit} onClick={handleSubmit}
          sx={{ py: 1.6, bgcolor: GREEN, '&:hover': { bgcolor: GREEN_D }, fontSize: 17 }}>
          {submitting ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : '確認訂位'}
        </Button>
      </Box>
    </Page>
  );
};

const Row = ({ label, value, emphasize }) => (
  <Stack direction="row" justifyContent="space-between">
    <Typography sx={{ fontSize: 16 }} color="text.secondary">{label}</Typography>
    <Typography sx={{ fontSize: 16, fontWeight: emphasize ? 700 : 500, color: emphasize ? GREEN : 'text.primary' }}>{value}</Typography>
  </Stack>
);

export default BookingPage;
